'use client';
import { createSharedPathnamesNavigation } from 'next-intl/navigation';
import type { HTMLAttributeAnchorTarget, PropsWithChildren, ReactNode } from 'react';
import { type ComponentProps, forwardRef } from 'react';

export type LinkShape = {
	label?: ReactNode;
	href?: string;
};

const { Link, redirect, usePathname, useRouter } = createSharedPathnamesNavigation({});
export { usePathname, useRouter };
export type AnchorProps = Omit<ComponentProps<'a'>, 'href' | 'ref'> & {
	href: string | undefined /** for debugging purposes */;
};

export const Anchor = forwardRef<HTMLAnchorElement, PropsWithChildren<AnchorProps>>(function Anchor({ children, href, ...props }, ref) {
	let scroll = false;
	let url: { href: string | undefined; onClick?: ComponentProps<'a'>['onClick'] | undefined } = {
		href: undefined,
		onClick: props.onClick,
	};

	if (typeof href === 'string') {
		url = { href };
	}

	if (!url.href) {
		return (
			<a
				ref={ref}
				{...props}
				onClick={url.onClick}
				href={url.onClick ? '#' : undefined}
				style={{ cursor: url.onClick ? 'pointer' : undefined }}
			>
				{children}
			</a>
		);
	}

	let target: HTMLAttributeAnchorTarget | undefined = props.target;
	if (target === undefined && href !== undefined && !(href.startsWith('/') || href.startsWith(process.env.NEXT_PUBLIC_SITE_URL))) {
		// VoCoVo wants all external links to open in a new tab

		target = '_blank';
	}

	if (url.href.includes('#')) {
		if (url.href.startsWith('#')) {
			const id = String(url.href).replace('#', '');
			url.href = '';
			url.onClick = event => {
				event.preventDefault();
				setTimeout(() => {
					const element = document.getElementById(id);
					element?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
				}, 0);
			};
			return (
				<a ref={ref} href={href} {...props} onClick={url.onClick} target={target}>
					{children}
				</a>
			);
		} else {
			scroll = true;
		}
	}

	if (target === '_blank') {
		return (
			<a ref={ref} href={url.href} {...props} target='_blank' rel='noopener noreferrer'>
				{children}
			</a>
		);
	}

	return (
		<Link ref={ref} href={url.href} onClick={url.onClick} {...props} scroll={scroll} prefetch={false}>
			<>{children}</>
		</Link>
	);
});
